import { type MaskStylesObj } from '@reactour/mask';
import { type PopoverStylesObj } from '@reactour/popover';
import { type StylesObj, TourProvider } from '@reactour/tour';
import { useAtomValue } from 'jotai';
import { type PropsWithChildren, useMemo } from 'react';

import { tutorialAtom } from '../../data-access/atoms/tutorial.atom';
import { useRTL } from '../../hooks/helpers/useRTL';
import { useTutorialStyles } from '../../hooks/tutorial/useTutorialStyles';

import { TutorialBackdropLayer } from './TutorialBackdropLayer';
import { NextButton, PreviousButton } from './TutorialButtons';

// Arrow CSS settings
import '@reactour/popover/dist/index.css';

export function TutorialProvider({ children }: PropsWithChildren) {
	const { isRTL } = useRTL();
	const { threshold } = useAtomValue(tutorialAtom);

	const {
		badgeStyle,
		clickAreaStyle,
		controlsStyle,
		highlightedAreaStyle,
		maskAreaStyle,
		maskWrapperStyle,
		popoverStyles
	} = useTutorialStyles();

	const defaultStyles: StylesObj & PopoverStylesObj & MaskStylesObj = useMemo(
		() => ({
			badge: badgeStyle(),
			clickArea: clickAreaStyle(),
			controls: controlsStyle(),
			highlightedArea: highlightedAreaStyle(),
			maskArea: maskAreaStyle(),
			maskWrapper: maskWrapperStyle(),
			popover: popoverStyles()
		}),
		[
			badgeStyle,
			clickAreaStyle,
			highlightedAreaStyle,
			maskAreaStyle,
			maskWrapperStyle,
			popoverStyles,
			controlsStyle
		]
	);

	return (
		<TourProvider
			badgeContent={({ currentStep, totalSteps }) => totalSteps > 1 && `${currentStep + 1}/${totalSteps}`}
			inViewThreshold={threshold ?? { x: 0, y: 30 }}
			maskId="tutorialMask"
			nextButton={NextButton}
			prevButton={PreviousButton}
			rtl={isRTL}
			scrollSmooth
			showBadge
			showCloseButton={false}
			showDots={false}
			steps={[]}
			styles={defaultStyles}
		>
			{children}
			<TutorialBackdropLayer />
		</TourProvider>
	);
}
